import React, { ReactNode, useEffect, useState } from 'react';

import clsx from 'clsx';
import { useFormContext } from 'react-hook-form';

import { PreQualificationStep, SignInStep } from 'prequalificationV2/actions';
import { Button } from 'components/Buttons/Button';
import FormBody from 'components/FormBody';
import { EyeIcon } from 'components/Icons';
import InfoMessage, { InfoMessageType } from 'components/InfoMessage';
import Input from 'components/Inputs/Input';
import PasswordRequirements from 'components/PasswordRequirements';
import { ButtonType } from 'enums/ButtonType';
import { removeSpaces } from 'prequalificationV2/utils/stringUtils';
import { getMessageForInvalidFields, isValidEmail } from 'prequalificationV2/utils/validationUtils';

import styles from './AccountForm.module.scss';
import passwordRequirements from './passwordRequirements';

export interface AccountFormData {
  email?: string;
  password?: string;
  confirmPassword?: string;
}

export interface AccountFormProps {
  isEmailDisabled?: boolean;
  passwordLabel?: string;
  showEmailField?: boolean;
  showPasswordField?: boolean;
  showConfirmPasswordField?: boolean;
  showPasswordCaption?: boolean;
  showForgotPassword?: boolean;
  showNotification?: boolean;
  notificationTitle?: (() => JSX.Element) | string;
  handleForgotPassword?: () => void;
  buttonLabel?: string;
  emailError?: string;
  passwordError?: string;
  onSubmit: (data: AccountFormData) => void;
  buttonRowClassname?: string;
  isNextButton?: boolean;
  backButton?: SignInStep | PreQualificationStep;
  backLink?: string;
  hintElement?: ReactNode | string;
}

const AccountForm = ({
  isEmailDisabled,
  passwordLabel,
  showEmailField,
  showPasswordField,
  showConfirmPasswordField,
  showPasswordCaption,
  showForgotPassword,
  showNotification,
  notificationTitle = '',
  handleForgotPassword,
  buttonLabel,
  emailError,
  passwordError,
  onSubmit,
  buttonRowClassname,
  isNextButton,
  backButton,
  backLink,
  hintElement,
}: AccountFormProps) => {
  const {
    register,
    formState: { errors, isSubmitted },
    setError,
    setValue,
    trigger,
    handleSubmit,
    watch,
    clearErrors,
  } = useFormContext();
  const watcher = watch();

  useEffect(() => {
    if (showEmailField && emailError) {
      setError('email', { type: 'manual', message: emailError });
    }

    if (showPasswordField && passwordError) {
      setError('password', { type: 'manual', message: passwordError });
    }
  }, [emailError, passwordError, showEmailField, showPasswordField, setError]);

  const [isPasswordValueVisible, setIsPasswordValueVisible] = useState(false);
  const [isConfirmPasswordValueVisible, setIsConfirmPasswordValueVisible] = useState(false);

  const classNamesToString = (...classes: Array<string>) => {
    return classes.reduce((accum: string, value: string) => `${accum} ${value}`);
  };

  const getEmailField = () => {
    return (
      <Input
        name="email"
        label="Email"
        inputContainerClassName={classNamesToString(styles.input, styles.inputEmail)}
        errorMessage={errors.email?.message}
        inputRef={
          register('email', {
            required: {
              value: !!showEmailField,
              message: 'Email is required',
            },
            validate: (value) => {
              return isValidEmail(value) || 'Please enter correct email';
            },
          }).ref
        }
        disabled={isEmailDisabled}
        onChange={(event: any) => {
          clearErrors();
          setValue('email', removeSpaces(event.target.value?.toLowerCase()));

          if (errors.email?.message) {
            trigger('email');
          }
        }}
        onBlur={() => {
          if (isSubmitted) {
            trigger('email');
          }
        }}
        value={watcher.email}
      />
    );
  };

  const getPasswordField = (isShowForgotPassword?: boolean) => {
    const showInlinePasswordRequirements = showPasswordCaption && !showEmailField && !showConfirmPasswordField;

    return (
      <div className={styles.passwordWrapper}>
        <Input
          name="password"
          label={passwordLabel}
          type={isPasswordValueVisible ? 'text' : 'password'}
          inputContainerClassName={styles.input}
          errorMessage={!showPasswordCaption ? errors.password?.message : ''}
          caption={
            showInlinePasswordRequirements ? (
              <PasswordRequirements
                items={passwordRequirements}
                password={watcher.password}
                error={errors.password?.message}
              />
            ) : undefined
          }
          onChange={(event: any) => {
            clearErrors();
            setValue('password', event.target.value);

            if (errors.password?.message) {
              trigger('password');
            }
          }}
          onBlur={() => {
            if (isSubmitted) {
              trigger('password');
            }
          }}
          inputRef={
            register('password', {
              required: {
                value: !!showPasswordField,
                message: 'Password is required',
              },
              validate: showPasswordCaption
                ? (password) =>
                    passwordRequirements.every((requirement) => requirement.isValid(password)) ||
                    getMessageForInvalidFields('Password')
                : undefined,
            }).ref
          }
          endAdornment={() => (
            <EyeIcon
              open={isPasswordValueVisible}
              className={styles.eyeIcon}
              onClick={() => setIsPasswordValueVisible(!isPasswordValueVisible)}
            />
          )}
          value={watcher.password}
        />
        {isShowForgotPassword && (
          <div className={styles.forgotPassword} onClick={handleForgotPassword}>
            Forgot password?
          </div>
        )}
      </div>
    );
  };

  const getConfirmPasswordField = () => {
    return (
      <Input
        name="confirmPassword"
        label="Confirm Password"
        type={isConfirmPasswordValueVisible ? 'text' : 'password'}
        inputContainerClassName={styles.input}
        errorMessage={errors.confirmPassword?.message}
        onChange={(event: any) => {
          setValue('confirmPassword', event.target.value);

          if (errors.confirmPassword?.message) {
            trigger('confirmPassword');
          }
        }}
        onBlur={() => {
          trigger('confirmPassword');
        }}
        value={watcher.confirmPassword}
        inputRef={
          register('confirmPassword', {
            required: {
              value: !!showConfirmPasswordField,
              message: 'Password is required',
            },
            validate: (password) => password === watcher.password || 'Entered passwords do not match',
          }).ref
        }
        endAdornment={() => (
          <EyeIcon
            open={isConfirmPasswordValueVisible}
            className={styles.eyeIcon}
            onClick={() => setIsConfirmPasswordValueVisible(!isConfirmPasswordValueVisible)}
          />
        )}
      />
    );
  };

  return (
    <>
      <FormBody onSubmit={handleSubmit(onSubmit)}>
        {hintElement && <div>{hintElement}</div>}
        {/* email field */}
        {showEmailField && !showPasswordField && !showConfirmPasswordField && (
          <div className={styles.formRow}>{getEmailField()}</div>
        )}

        {/* email and password fields */}
        {showEmailField && showPasswordField && !showConfirmPasswordField && (
          <div className={styles.formRow}>
            {getEmailField()}
            {getPasswordField(showForgotPassword)}
          </div>
        )}

        {/* only password field */}
        {showPasswordField && !showEmailField && !showConfirmPasswordField && getPasswordField(showForgotPassword)}

        {/* create account page */}
        {showEmailField && showPasswordField && showConfirmPasswordField && (
          <div className={styles.createAccountForm}>
            <div className={clsx(styles.createAccountFormRow, styles.mobileMarginBottom24)}>
              {getEmailField()}
              {getPasswordField()}
              {getConfirmPasswordField()}
            </div>
            <div className={clsx(styles.passwordRequirements)}>
              <PasswordRequirements
                items={passwordRequirements}
                password={watcher.password}
                error={errors.password?.message}
              />
            </div>
          </div>
        )}

        {showNotification && (
          <InfoMessage
            title={notificationTitle}
            type={InfoMessageType.Attention}
            containerClassname={styles.infoMessageContainer}
            iconClassname={styles.infoMessageIcon}
          />
        )}
      </FormBody>

      <div
        className={clsx(styles.buttonRow, buttonRowClassname, { [styles.onlyOneButton]: !(backButton || backLink) })}
      >
        {(backButton || backLink) && (
          <Button buttonType={ButtonType.Secondary} applicationStep={backButton} link={backLink} withArrow>
            Back
          </Button>
        )}
        {isNextButton ? (
          <Button type="submit" form="form" withArrow>
            Next
          </Button>
        ) : (
          <Button type="submit" form="form">
            {buttonLabel}
          </Button>
        )}
      </div>
    </>
  );
};

export default AccountForm;
